import type { ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CalendarIcon from '../assets/calendar.svg?react';
import DashboardIcon from '../assets/dashboard.svg?react';
import DocumentIcon from '../assets/document.svg?react';
import InvoicesIcon from '../assets/invoices.svg?react';
import JournalIcon from '../assets/journal.svg?react';
import LeagueIcon from '../assets/league.svg?react';
import ReviewIcon from '../assets/review.svg?react';
import TestIcon from '../assets/test.svg?react';
import UserIcon from '../assets/user.svg?react';
import VideoIcon from '../assets/video.svg?react';

import type { Permission } from '@/hooks/useCheckPermission';
import { useAppConfigQuery } from '@/services/app';

type MenuItem = { title: string; link: string };

type SubItem = MenuItem & { permissions: Permission[] };

type MenuConfig = MenuItem & { icon: ReactNode } & (
    | { subItems: SubItem[]; permissions?: never }
    | { subItems?: undefined; permissions: Permission[] }
  );

const SVG_ITEM_PROPS = {
  className: 'fill-current',
  width: 22,
  height: 22,
} as const;

const useSidebarConfig = () => {
  const appConfigQuery = useAppConfigQuery();
  const { t } = useTranslation();

  const MENU_ITEMS: MenuConfig[] = [
    {
      title: t('msg_sidebar_home_page'),
      link: '/',
      icon: <DashboardIcon {...SVG_ITEM_PROPS} />,
      permissions: ['user'],
    },
    {
      title: t('msg_sidebar_admin_page'),
      link: '/video',
      icon: <JournalIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        { title: t('msg_sidebar_button_add_game'), link: '/games/new', permissions: ['games:admin'] },
        {
          title: t('msg_sidebar_game_nominations'),
          link: '/games/assign',
          permissions: ['games:admin'],
        },
        { title: t('msg_sidebar_button_game_upload'), link: '/games/import', permissions: ['games:admin'] },
        { title: t('msg_sidebar_button_add_post'), link: '/posts/new', permissions: ['posts:admin'] },
        { title: t('msg_sidebar_button_balance'), link: '/balance', permissions: ['admin', 'balance'] },
        { title: t('msg_sidebar_reports'), link: '/admin/reports', permissions: ['games:admin'] },
        { title: t('msg_sidebar_game_documents'), link: '/game-documents/all', permissions: ['game-documents:admin'] },
        { title: t('msg_sidebar_identity_cards'), link: '/identity-cards', permissions: ['identity-cards:admin'] },
      ],
    },

    {
      title: t('msg_sidebar_my_schedule'),
      link: '/schedule/my',
      icon: <CalendarIcon {...SVG_ITEM_PROPS} />,
      permissions: ['user'],
    },
    {
      title: t('msg_sidebar_my_availability'),
      link: '/availability',
      icon: <UserIcon {...SVG_ITEM_PROPS} />,
      permissions: ['user', 'availability'],
    },
    {
      title: t('msg_sidebar_schedule_page'),
      link: '/schedule',
      icon: <CalendarIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        { title: t('msg_sidebar_full_schedule'), link: '/schedule/full', permissions: ['user', 'schedule:full'] },
        { title: t('msg_sidebar_button_add_game'), link: '/games/new', permissions: ['games:admin'] },
        { title: t('msg_sidebar_button_game_upload'), link: '/games/import', permissions: ['games:admin'] },
        { title: t('msg_sidebar_schedule_export'), link: '/schedule/export', permissions: ['games:admin'] },
        {
          title: t('msg_sidebar_game_nominations'),
          link: '/games/assign',
          permissions: ['games:admin'],
        },
        { title: t('msg_sidebar_unconfirmed_games'), link: '/games/unconfirmed', permissions: ['games:admin'] },
      ],
    },

    {
      title: t('msg_sidebar_users_page'),
      link: '/users',
      subItems: [
        { title: t('msg_sidebar_items_list'), link: '/users', permissions: ['user', 'user:full'] },
        {
          title: t('msg_sidebar_users_availability'),
          link: '/availability/users-availability',
          permissions: ['availability:admin'],
        },
        { title: t('msg_sidebar_journal_users_ranking'), link: '/journal/users-ranking', permissions: ['admin'] },
      ],
      icon: <UserIcon {...SVG_ITEM_PROPS} />,
    },

    {
      title: t('msg_sidebar_leagues_page'),
      link: '/leagues',
      icon: <LeagueIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        { title: t('msg_sidebar_items_list'), link: '/leagues', permissions: ['user', 'leagues:full'] },

        {
          title: t('msg_sidebar_league_groups_page'),
          link: '/leagues/league-groups',
          permissions: ['admin', 'league-groups'],
        },
        {
          title: t('msg_sidebar_league_categories_page'),
          link: '/leagues/categories',
          permissions: ['admin'],
        },
        {
          title: t('msg_sidebar_game_trip_rankings_page'),
          link: '/leagues/trip-rankings',
          permissions: ['admin', 'trip-ranking'],
        },
      ],
    },

    {
      title: t('msg_sidebar_journal_page'),
      link: '/journal/my',
      icon: <InvoicesIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        { title: t('msg_sidebar_journal_my'), link: '/journal/my', permissions: ['user'] },
        { title: t('msg_sidebar_journal_my_ranking'), link: '/journal/ranking', permissions: ['user'] },
        { title: t('msg_sidebar_journal_my_expenses'), link: '/journal/expenses', permissions: ['expenses:report'] },
        { title: t('msg_sidebar_journal_export'), link: '/journal/export', permissions: ['journal:export'] },
      ],
    },
    {
      title: t('msg_sidebar_invoices'),
      link: '/invoices',
      permissions: ['user', 'invoices'],
      icon: <InvoicesIcon {...SVG_ITEM_PROPS} />,
    },
    {
      title: t('msg_sidebar_documents_page'),
      link: '/documents',
      permissions: ['documents'],
      icon: <DocumentIcon {...SVG_ITEM_PROPS} />,
    },
    {
      title: t('msg_sidebar_videos_page'),
      link: '/video',
      icon: <VideoIcon {...SVG_ITEM_PROPS} />,
      permissions: ['video'],
    },
    {
      title: t('msg_sidebar_tests_page'),
      link: '/tests',
      icon: <TestIcon {...SVG_ITEM_PROPS} />,
      permissions: ['tests'],
    },
    {
      title: t('msg_sidebar_internal_review_page'),
      link: '/internal-reviews',
      icon: <ReviewIcon {...SVG_ITEM_PROPS} />,
      permissions: ['internal-review'],
    },
    {
      title: t('msg_label_referee_evaluation_statistics'),
      link: '/referee-evaluation/statistics',
      icon: <ReviewIcon {...SVG_ITEM_PROPS} />,
      permissions: ['referee-evaluation:view'],
    },
    {
      title: t('msg_sidebar_internal_videos_page'),
      link: '/internal-videos/my',
      icon: <VideoIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        {
          title: t('msg_sidebar_button_my_internal_videos'),
          link: '/internal-videos/my',
          permissions: ['internal-videos:view'],
        },
        {
          title: t('msg_sidebar_button_all_internal_videos'),
          link: '/internal-videos/all',
          permissions: ['internal-videos:admin'],
        },
        {
          title: t('msg_sidebar_button_inter_video_categories'),
          link: '/internal-videos/category',
          permissions: ['internal-videos:admin'],
        },
      ],
    },

    {
      title: t('msg_sidebar_internal_documents_page'),
      link: '/internal-documents/my',
      icon: <DocumentIcon {...SVG_ITEM_PROPS} />,
      subItems: [
        {
          title: t('msg_sidebar_button_my_internal_documents'),
          link: '/internal-documents/my',
          permissions: ['internal-documents:view'],
        },
        {
          title: t('msg_sidebar_button_all_internal_documents'),
          link: '/internal-documents/all',
          permissions: ['internal-documents:admin'],
        },
      ],
    },
  ];

  const customMenuItems =
    appConfigQuery.data?.customMenu.map((menu) => {
      return {
        title: menu.title,
        link: menu.link,
        icon: <DocumentIcon {...SVG_ITEM_PROPS} />,
        permissions: ['user'],
      } as MenuConfig;
    }) ?? [];

  return [...MENU_ITEMS, ...customMenuItems];
};

export default useSidebarConfig;
